import { Link } from "react-router-dom";
import { ArrowRight, Mail } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import SectionHeading from "@/components/SectionHeading";
import { company } from "@/data/site";
import { useT, useContent } from "@/i18n";

export default function Services() {
  const t = useT();
  const { services } = useContent();
  const steps = t("services.steps");

  return (
    <div data-testid="services-page">
      <PageHeader
        title={t("services.headerTitle")}
        crumb={t("nav.services")}
        subtitle={`${services.title}${t("services.headerSubtitleSuffix")}`}
        image="/sections/hero-industrial.jpg"
      />

      <section className="py-28 md:py-36 bg-paper">
        <div className="container-kyt">
          <SectionHeading eyebrow={t("services.eyebrow")} title={services.title} desc={services.intro} />
          <div className="mt-16 grid md:grid-cols-2 gap-x-16">
            {services.items.map((s, i) => (
              <Reveal key={s} delay={(i % 2) * 70}>
                <div className="group flex items-baseline gap-6 py-6 border-t border-ink/12">
                  <span className="font-mono text-[12px] text-slate-400 group-hover:text-brand-blue transition-colors w-7 shrink-0">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <span className="font-display text-[18px] text-ink group-hover:translate-x-1.5 transition-transform duration-300">
                    {s}
                  </span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="py-28 md:py-36 bg-white border-y border-ink/10">
        <div className="container-kyt grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
          <Reveal>
            <div className="aspect-[4/3] overflow-hidden">
              <img src="/sections/hero-industrial.jpg" alt="KYT" className="h-full w-full object-cover" />
            </div>
          </Reveal>
          <div>
            <SectionHeading eyebrow={t("services.whyEyebrow")} title={t("services.whyTitle")} />
            <div className="mt-10 space-y-px">
              {steps.map((step, i) => (
                <Reveal key={step.t} delay={i * 90}>
                  <div className="flex gap-7 py-6 border-t border-ink/12">
                    <div className="font-mono text-sm text-brand-blue w-8 shrink-0 pt-1">0{i + 1}</div>
                    <div>
                      <h4 className="font-display text-lg font-semibold text-ink">{step.t}</h4>
                      <p className="text-[14.5px] text-slate-500 mt-1.5">{step.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy-900">
        <div className="container-kyt py-24 md:py-28 flex flex-col md:flex-row items-start md:items-center justify-between gap-10">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-[2.6rem] font-bold text-white leading-[1.05] max-w-2xl">{t("services.ctaTitle")}</h2>
          </Reveal>
          <Reveal delay={120} className="flex flex-col sm:flex-row gap-8 shrink-0">
            <a href={`mailto:${company.email}?subject=${encodeURIComponent(t("common.getQuote"))}`} className="group inline-flex items-center gap-3 text-white text-[15px] font-medium">
              <Mail className="h-5 w-5 text-brand-electric" />
              <span className="border-b border-white/40 pb-1.5 group-hover:border-brand-electric transition-colors">{t("common.emailSend")}</span>
            </a>
            <Link to="/iletisim" className="group inline-flex items-center gap-3 text-white text-[15px] font-medium">
              <span className="border-b border-white/40 pb-1.5 group-hover:border-brand-electric transition-colors">{t("common.contactCta")}</span>
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
